import * as React from "react";
import {
  Box,
  Button,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { main_button } from "../app/components/Styles";
import { Pagination } from "antd";
import { getNotifications, getNotificationsCount, viewNotification } from "../app/store/actions/dataActions";
import { useEffect } from "react";
import SearchFilterBar, { FilterChips } from "../app/glass/SearchFilterBar";
import { pageTitle, formatInt, StatusPill } from "../app/glass/styles";
import { splitRelativeDate } from "../app/glass/OrderTableCells";
import Hoverable from "../app/glass/Hoverable";
import { useTranslation } from 'react-i18next';

const VIEW_CHIPS = [
  ['', 'common.all'],
  ['false', 'notifications.unread'],
  ['true', 'notifications.read'],
];

function previewText(content, max = 160) {
  const text = String(content || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
}

export const Notifications = ({
  getNotifications,
  getNotificationsCount,
  viewNotification,
  notifications,
  notificationsCount,
  isLoading,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [viewed, setViewed] = React.useState(searchParams.get('viewed') || '');
  const [expandedId, setExpandedId] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const listParams = React.useCallback((overrides = {}) => {
    const pageToUse = overrides.page !== undefined ? overrides.page : page;
    const pageSizeToUse = overrides.pageSize !== undefined ? overrides.pageSize : pageSize;
    const searchToUse = overrides.search !== undefined ? overrides.search : search;
    const viewedToUse = overrides.viewed !== undefined ? overrides.viewed : viewed;
    return {
      page: pageToUse,
      limit: pageSizeToUse,
      ...(searchToUse && { search: searchToUse }),
      ...(viewedToUse !== '' && { viewed: viewedToUse }),
    };
  }, [page, pageSize, search, viewed]);

  const syncParams = (updates) => {
    const next = {
      page: String(updates.page ?? page),
      pageSize: String(updates.pageSize ?? pageSize),
      ...(updates.search || search ? { search: updates.search !== undefined ? updates.search : search } : {}),
      ...(updates.viewed !== undefined ? (updates.viewed ? { viewed: updates.viewed } : {}) : (viewed ? { viewed } : {})),
    };
    Object.keys(next).forEach((key) => {
      if (!next[key]) delete next[key];
    });
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    getNotifications(listParams());
  }, [page, pageSize, viewed]);

  const handleSearch = () => {
    setPage(1);
    setExpandedId(null);
    syncParams({ page: 1, search });
    getNotifications(listParams({ page: 1, search }));
  };

  const handleViewedChange = (nextViewed) => {
    setViewed(nextViewed);
    setPage(1);
    setExpandedId(null);
    syncParams({ page: 1, viewed: nextViewed });
  };

  const handlePageChange = (nextPage, nextSize) => {
    setPage(nextPage);
    setPageSize(nextSize);
    setExpandedId(null);
    syncParams({ page: nextPage, pageSize: nextSize });
  };

  const rows = (notifications?.docs || []).map((row) => ({
    _id: row.notification?._id || row._id,
    viewId: row._id,
    content: row.notification?.content || '',
    isRead: Boolean(row.viewed),
    createdAt: row.createdAt,
  }));

  const handleToggle = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const handleMarkRead = (notificationId) => {
    viewNotification({ notificationId });
    getNotifications(listParams());
    getNotificationsCount({});
  };

  const unreadCount = Number(notificationsCount) || 0;
  const totalCount = notifications?.totalDocs || 0;

  const kpiItems = [
    {
      label: t('notifications.unreadNotifications'),
      value: formatInt(unreadCount),
      sub: t('notifications.unread'),
    },
    {
      label: t('notifications.totalNotifications'),
      value: formatInt(totalCount),
      sub: viewed ? t('notifications.inThisView') : t('common.total'),
    },
  ];

  return (
    <div>
      <div style={{ ...pageTitle, marginTop: 6 }}>{t('navigation.notifications')}</div>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          className="cz-g2"
          style={{
            background: 'var(--card-solid)',
            borderRadius: 26,
            padding: isMobile ? '18px 20px' : '24px 34px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, minmax(0, 280px))',
            gap: 24,
            alignItems: 'center',
            border: '1px solid var(--s12)',
            boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
          }}
        >
          {kpiItems.map((st) => (
            <div key={st.label}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{st.label}</div>
              <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 500 }}>
                {isLoading ? <Skeleton width={48} /> : st.value}
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut2)', marginTop: 6 }}>{st.sub}</div>
            </div>
          ))}
        </div>

        <FilterChips
          value={viewed}
          onChange={handleViewedChange}
          options={VIEW_CHIPS.map(([value, labelKey]) => [value, t(labelKey)])}
        />

        <SearchFilterBar
          placeholder={t('notifications.searchPlaceholder')}
          value={search}
          onChange={setSearch}
          onSearch={handleSearch}
          isMobile={isMobile}
        />

        <div
          style={{
            background: 'var(--card-solid)',
            borderRadius: 24,
            border: '1px solid var(--s10)',
            boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
            overflow: 'hidden',
          }}
        >
          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[0, 1, 2, 3].map((key) => (
                <div key={key} style={{ padding: isMobile ? '16px 18px' : '18px 24px', borderBottom: '1px solid var(--s08)' }}>
                  <Skeleton width="72%" height={18} />
                  <Skeleton width="40%" height={14} sx={{ mt: 1 }} />
                </div>
              ))}
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div
              style={{
                padding: '48px 24px',
                textAlign: 'center',
                color: 'var(--mut)',
                fontSize: 13.5,
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--tx2)', marginBottom: 6 }}>
                {t('notifications.noNotifications')}
              </div>
              {t('notifications.emptyHint')}
            </div>
          )}

          {!isLoading && rows.map((row, index) => {
            const expanded = expandedId === row._id;
            const when = splitRelativeDate(row.createdAt);
            return (
              <Hoverable
                key={row.viewId || row._id}
                base={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'start',
                  padding: isMobile ? '16px 18px' : '18px 24px',
                  border: 'none',
                  borderBottom: index === rows.length - 1 ? 'none' : '1px solid var(--s08)',
                  background: expanded ? 'var(--s04)' : 'transparent',
                  cursor: 'pointer',
                  color: 'inherit',
                  fontFamily: 'inherit',
                }}
                hover={{ background: 'var(--s05)' }}
                onClick={() => handleToggle(row._id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleToggle(row._id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      marginTop: 7,
                      flexShrink: 0,
                      background: row.isRead ? 'var(--s18)' : 'var(--red-tx2)',
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: row.isRead ? 500 : 600,
                        color: 'var(--tx)',
                        lineHeight: 1.45,
                        whiteSpace: expanded ? 'pre-wrap' : 'normal',
                      }}
                    >
                      {expanded ? row.content : previewText(row.content) || t('notifications.noNotifications')}
                    </div>
                    {expanded && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                        {!row.isRead && (
                          <Button
                            size="small"
                            sx={{ ...main_button, textTransform: 'none', py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMarkRead(row._id);
                            }}
                          >
                            {t('notifications.markAsRead')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                    <StatusPill status={row.isRead ? 'Read' : 'In Progress'}>
                      {row.isRead ? t('notifications.read') : t('notifications.unread')}
                    </StatusPill>
                    <div style={{ fontSize: 11, color: 'var(--mut)', textAlign: 'end' }}>
                      <div>{when.day}</div>
                      <div>{when.time}</div>
                    </div>
                  </div>
                </div>
              </Hoverable>
            );
          })}
        </div>

        {totalCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={totalCount}
              showSizeChanger={false}
              showTotal={(total) => `${t('table.total')} ${total} ${t('notifications.notifications')}`}
              onChange={handlePageChange}
            />
          </div>
        )}
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.records?.isLoading,
  notifications: state?.records?.notifications,
  notificationsCount: state?.records?.notificationsCount,
});

const mapDispatchToProps = { getNotifications, getNotificationsCount, viewNotification };

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
